import { useMutation, useQueryClient } from "@tanstack/react-query";
import { onAddNotifications, onNotificationsHidden, onNotificationsRead, onNotificationsUnread, onSetNotificationBadge } from "common/signalR";
import { NoDataAvailableDiv } from "components/NoDataAvailableDiv";
import { Error, Loading } from "components/panels";
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useInfiniteScrollQuery } from "utilities/hookutils";
import NotificationCard from "./NotificationCard";
import classNames from "classnames";

interface NotificationListProps {
  onLoad: (filters: server.dto.ListNotificationFilters) => void
  for: "list" | "popup"
  status?: server.dto.NotificationStatusFilterSettings
}

export interface NotificationListRef {
  markAllAsRead: () => void;
}

const NotificationList = forwardRef<NotificationListRef, NotificationListProps>((props: NotificationListProps, forwardedRef) => {
  const queryClient = useQueryClient();
  const [dontShowNotificationIds, setDontShowNotificationIds] = useState<number[]>([]);

  const { query, items: notifications, loadMoreRef } = useInfiniteScrollQuery(
    ["notifications", props.status],
    page => window.CoreApi.Notification.Paginated({
      markAllSeen: true,
      channel: "browser",
      notificationStatus: props.status || "noFilter",
      page,
      perPage: 25
    })
  );

  const firstPage = query.data?.pages[0];

  useEffect(() => {
    if (firstPage == null) return;
    props.onLoad(firstPage.request);
  }, [firstPage]);

  useImperativeHandle(forwardedRef, () => ({
    markAllAsRead: () => {
      markAllRead.mutate();
    }
  }), [])

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ["notifications"], fetchStatus: "idle" })
  }, [props.status]);

  useEffect(() => {
    const { removeEvent } = onSetNotificationBadge(async () => queryClient.invalidateQueries({ queryKey: ["notifications"], fetchStatus: "idle" }));
    const { removeEvent: removeEvent2 } = onAddNotifications(async () => queryClient.invalidateQueries({ queryKey: ["notifications"], fetchStatus: "idle" }));
    const { removeEvent: removeEvent3 } = onNotificationsHidden(async () => queryClient.invalidateQueries({ queryKey: ["notifications"], fetchStatus: "idle" }));
    const { removeEvent: removeEvent4 } = onNotificationsRead(async () => queryClient.invalidateQueries({ queryKey: ["notifications"], fetchStatus: "idle" }));
    const { removeEvent: removeEvent5 } = onNotificationsUnread(async () => queryClient.invalidateQueries({ queryKey: ["notifications"], fetchStatus: "idle" }));

    return () => {
      removeEvent();
      removeEvent2();
      removeEvent3();
      removeEvent4();
      removeEvent5();
    };
  }, []);

  const hideNotification = useMutation({
    mutationFn: async (notification: server.dto.Notification) => window.CoreApi.Notification.Hide([notification.notificationId]),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ["notifications"], fetchStatus: "idle" })
  });

  const markRead = useMutation({
    mutationFn: async (notification: server.dto.Notification) => window.CoreApi.Notification.MarkRead([notification.notificationId], true),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ["notifications"], fetchStatus: "idle" })
  });

  const markAllRead = useMutation({
    mutationFn: async () => window.CoreApi.Notification.MarkAllRead("browser"),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ["notifications"], fetchStatus: "idle" })
  });

  const visibleNotifications = notifications
    .sort((a, b) => b.notificationId - a.notificationId)
    .filter(n => !n.hidden && !dontShowNotificationIds.includes(n.notificationId));

  return <>
    {query.status === "success" && notifications.length === 0 && (<div className={classNames("notification-card-list", {
      "notification-card-list-popup": props.for === "popup",
    })}>
      <NoDataAvailableDiv />
    </div>)}
    {query.status === "success" && notifications.length > 0 && (<div className={classNames("notification-card-list", {
      "notification-card-list-popup": props.for === "popup",
    })}>
      {visibleNotifications.map((notification, index) =>
        <React.Fragment key={notification.notificationId}>
          {index === visibleNotifications.length - 3 && (<div ref={loadMoreRef} />)}
          <NotificationCard
            key={notification.notificationId}
            for={props.for}
            notification={notification}
            onRead={() => {
              if (!notification.read) {
                markRead.mutate(notification);
              }
            }}
            onDismiss={() => {
              hideNotification.mutate(notification);
              setDontShowNotificationIds([...dontShowNotificationIds, notification.notificationId]);
            }}
          />
          {(index === visibleNotifications.length - 1 && !query.hasNextPage) && <div className="notification-no-more-items">
            {RESX.Notifications.NoMoreNotifications}
          </div>}
        </React.Fragment>
      )}
    </div>)}
    {(query.isLoading || query.isFetchingNextPage) && (<Loading />)}
    {query.status === "error" && (<Error />)}
  </>;
});

export default NotificationList;
